import { useCallback, useEffect, useRef, useState } from "react";
import { getAppData } from "../../api/endpoints.ts";
import type { AppConfig } from "./appRegistry.ts";
import { StatusIcon } from "../../components/ui/statusIcon.tsx";
import {
  DonutChart,
  BarChart,
  StackedBarChart,
  LineChart,
  getColor,
  type DonutSlice,
  type BarDatum,
  type StackedBarDatum,
  type LineSeries,
} from "../../components/chart/charts.tsx";
import styles from "./classicDashboard.module.scss";

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Row = Record<string, any>;

interface AppWidgetProps {
  appName: string;
  config: AppConfig;
}

export function AppWidget({ appName, config }: AppWidgetProps) {
  const [data, setData] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);

  const fetchData = useCallback(async () => {
    if (config.kind === "image" || config.kind === "stub") {
      setLoading(false);
      return;
    }
    try {
      const result = await getAppData(appName);
      if (result.bError) {
        setError(result.sMessage ?? "Failed to load");
        return;
      }
      const raw = result.oData;
      const parsed: Row[] = typeof raw === "string" ? JSON.parse(raw) : Array.isArray(raw) ? raw : [];
      setData(parsed);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  }, [appName, config.kind]);

  useEffect(() => {
    fetchData();
    timerRef.current = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [fetchData]);

  return (
    <div className={styles.appCard}>
      <div className={styles.appHeader}>{config.title}</div>
      <div className={styles.appBody}>
        {loading && <span className={styles.appLoading}>Loading…</span>}
        {error && <span className={styles.appError}>{error}</span>}
        {!loading && !error && renderBody(config, data)}
      </div>
    </div>
  );
}

function renderBody(config: AppConfig, data: Row[] | null): React.ReactNode {
  switch (config.kind) {
    case "donut":
      return renderDonut(config, data ?? []);
    case "bar":
      return renderBar(config, data ?? []);
    case "dualBar":
      return renderDualBar(config, data ?? []);
    case "stackedBar":
      return renderStackedBar(config, data ?? []);
    case "line":
    case "area":
      return renderLine(config, data ?? []);
    case "datatable":
      return renderTable(config, data ?? []);
    case "metrics":
      return renderMetrics(config, data ?? []);
    case "image":
      return <span className={styles.appLoading}>Demo — static preview</span>;
    case "stub":
      return <span className={styles.appLoading}>Not yet implemented</span>;
    default:
      return null;
  }
}

// ── Donut ────────────────────────────────────────────────

function renderDonut(config: AppConfig, data: Row[]) {
  if (!config.donut || data.length === 0) return <EmptyState />;
  const { value, color, label, tooltip } = config.donut;
  const slices: DonutSlice[] = data.map((r) => ({
    label: String(r[label] ?? ""),
    value: Number(r[value]) || 0,
    color: r[color] as string | undefined,
    tooltip: config.tooltipFn?.(r) ?? (tooltip ? String(r[tooltip] ?? "") : undefined),
  }));

  return (
    <div className={styles.chartContainer}>
      <DonutChart data={slices} />
      <ul className={styles.legend}>
        {data.map((r, i) => (
          <li key={i} className={styles.legendItem}>
            <span className={styles.legendSwatch} style={{ background: (r[color] as string) ?? getColor(i) }} />
            {config.donut!.legendFn ? config.donut!.legendFn(r) : `${r[label]} (${r[value]})`}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Bar ──────────────────────────────────────────────────

function renderBar(config: AppConfig, data: Row[]) {
  if (!config.bar || data.length === 0) return <EmptyState />;
  const { value, label } = config.bar;
  const items: BarDatum[] = data.map((r, i) => ({
    label: String(r[label] ?? i),
    value: Number(r[value]) || 0,
    tooltip: config.tooltipFn?.(r),
  }));

  return (
    <div className={styles.barChartWrap}>
      <BarChart data={items} />
    </div>
  );
}

// ── Dual Bar (top / bottom) ──────────────────────────────

function renderDualBar(config: AppConfig, data: Row[]) {
  if (!config.dualBar || data.length === 0) return <EmptyState />;
  const { value, label, scoreField } = config.dualBar;
  const top = data
    .filter((r) => String(r[scoreField]).toUpperCase() === "TOP")
    .map((r) => ({
      label: String(r[label] ?? ""),
      value: Number(r[value]) || 0,
      color: "#7ed500",
      tooltip: config.tooltipFn?.(r),
    }));
  const bottom = data
    .filter((r) => String(r[scoreField]).toUpperCase() === "BOTTOM")
    .map((r) => ({
      label: String(r[label] ?? ""),
      value: Number(r[value]) || 0,
      color: "#E4002B",
      tooltip: config.tooltipFn?.(r),
    }));

  return (
    <div className={styles.barChartWrap}>
      {top.length > 0 && (
        <>
          <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginBottom: 4 }}>Top</div>
          <BarChart data={top} color="#7ed500" />
        </>
      )}
      {bottom.length > 0 && (
        <>
          <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-muted)", marginTop: 8, marginBottom: 4 }}>
            Bottom
          </div>
          <BarChart data={bottom} color="#E4002B" />
        </>
      )}
    </div>
  );
}

// ── Stacked Bar ──────────────────────────────────────────

function renderStackedBar(config: AppConfig, data: Row[]) {
  if (!config.stackedBar || data.length === 0) return <EmptyState />;
  const { labelField, series } = config.stackedBar;
  const items: StackedBarDatum[] = data.map((r) => ({
    label: String(r[labelField] ?? ""),
    tooltip: config.tooltipFn?.(r),
    segments: series.map((s) => ({
      value: Number(r[s.field]) || 0,
      color: s.color,
      tooltip: `${s.label}: ${r[s.field]}`,
    })),
  }));

  return (
    <div className={styles.barChartWrap}>
      <StackedBarChart data={items} />
      <ul className={styles.legend} style={{ flexDirection: "row", flexWrap: "wrap", gap: "var(--space-2)", marginTop: 8 }}>
        {series.map((s, i) => (
          <li key={i} className={styles.legendItem}>
            <span className={styles.legendSwatch} style={{ background: s.color }} />
            {s.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Line / Area ──────────────────────────────────────────

function renderLine(config: AppConfig, data: Row[]) {
  if (!config.line || data.length === 0) return <EmptyState />;
  const { labelField, series: seriesCfg, splitSeries } = config.line;

  if (splitSeries) {
    const splitHeight = Math.round(250 * 0.55);
    return (
      <div className={styles.barChartWrap} style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        {seriesCfg.map((s, i) => {
          const single: LineSeries[] = [{
            data: data.map((r) => ({
              label: String(r[labelField] ?? ""),
              value: Number(r[s.field]) || 0,
              tooltip: config.tooltipFn?.(r),
            })),
            color: s.color,
            fill: s.fill ?? config.kind === "area",
          }];
          return (
            <div key={i}>
              <div style={{ fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", marginBottom: 2, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: s.color }} />
                {s.label}
              </div>
              <LineChart series={single} height={splitHeight} showLabels={config.line!.showLabels ?? true} />
            </div>
          );
        })}
      </div>
    );
  }

  const chartSeries: LineSeries[] = seriesCfg.map((s) => ({
    data: data.map((r) => ({
      label: String(r[labelField] ?? ""),
      value: Number(r[s.field]) || 0,
      tooltip: config.tooltipFn?.(r),
    })),
    color: s.color,
    fill: s.fill ?? config.kind === "area",
  }));

  return (
    <div className={styles.barChartWrap}>
      <LineChart series={chartSeries} showLabels={config.line.showLabels ?? true} />
      <ul className={styles.legend} style={{ flexDirection: "row", flexWrap: "wrap", gap: "var(--space-2)", marginTop: 8 }}>
        {seriesCfg.map((s, i) => (
          <li key={i} className={styles.legendItem}>
            <span className={styles.legendSwatch} style={{ background: s.color }} />
            {s.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Datatable ────────────────────────────────────────────

function renderTable(config: AppConfig, data: Row[]) {
  if (!config.table || data.length === 0) return <EmptyState />;
  const columns = config.table;
  return (
    <div style={{ width: "100%", overflowX: "auto", alignSelf: "flex-start" }}>
      <table className={styles.appTable}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.field}>{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.field}>
                  {c.field === "Read"
                    ? <StatusIcon kind={row[c.field] ? "mailRead" : "mailUnread"} size={15} label={row[c.field] ? "Read" : "Unread"} />
                    : String(row[c.field] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Metrics / KPI labels ─────────────────────────────────

function renderMetrics(config: AppConfig, data: Row[]) {
  if (!config.metric || data.length === 0) return <EmptyState />;
  const { typeField, valueFields } = config.metric;

  return (
    <div className={styles.metricGrid}>
      {data.map((row, i) => (
        <div key={i} style={{ display: "contents" }}>
          <div className={styles.metricItem} style={{ gridColumn: "1 / -1" }}>
            <span
              className={styles.metricLabel}
              style={{ fontWeight: 600 }}
              title={config.metricTooltips?.[String(row[typeField] ?? "")] ?? undefined}
            >
              {String(row[typeField] ?? "")}
            </span>
          </div>
          {valueFields.map((vf) => (
            <div key={vf.field} className={styles.metricItem}>
              <span className={styles.metricValue}>{String(row[vf.field] ?? "—")}</span>
              <span
                className={styles.metricLabel}
                title={config.metricTooltips?.[vf.label] ?? undefined}
              >
                {vf.label}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Empty state ──────────────────────────────────────────

function EmptyState() {
  return <span className={styles.appLoading}>No data available</span>;
}
